import React, { FC, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { Button } from "@natera/form";
import { BlueHeaderContent } from "@app/components/ui/layout";
import { GisIntakeStep } from "@app/service/intakeForm";
import { useBookingConfirmedRedirect } from "../useBookingConfirmedRedirect";
import { useIntakeForm } from "../context";
import "../intakeFormSteps.scss";

const messages = defineMessages({
  pageTitle: {
    id: "intakeFormStep.pageTitle",
    defaultMessage: "Genetic Information Session",
  },
  stepIndicator: {
    id: "intakeFormStep.stepIndicator",
    defaultMessage: "Step {current} of {total}",
  },
  continueButton: {
    id: "intakeFormStep.continueButton",
    defaultMessage: "Continue",
  },
  backButton: {
    id: "intakeFormStep.backButton",
    defaultMessage: "Back",
  },
  backButtonAria: {
    id: "intakeFormStep.backButtonAria",
    defaultMessage: "Back to previous step",
  },
});

interface IntakeFormStepLayoutProps {
  currentStep: GisIntakeStep;
  totalSteps: number;
  title: string;
  subtitle?: string;
  children: ReactNode;
  onContinue: () => void;
  onBack?: () => void;
  continueDisabled?: boolean;
  continueLabel?: string;
  showBackButton?: boolean;
  showBackLink?: boolean;
}

const IntakeFormStepLayout: FC<IntakeFormStepLayoutProps> = ({
  currentStep,
  totalSteps,
  title,
  subtitle,
  children,
  onContinue,
  onBack,
  continueDisabled = false,
  continueLabel,
  showBackButton = true,
  showBackLink = true,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { getStepNumber } = useIntakeForm();

  // Once the appointment is booked the intake answers are locked; bounce the
  // patient out of any editable step so they can't navigate back and edit.
  const bookingConfirmedRedirect = useBookingConfirmedRedirect();

  const stepNumber = getStepNumber(currentStep);
  const progressPercentage = (stepNumber / totalSteps) * 100;

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  if (bookingConfirmedRedirect) {
    return bookingConfirmedRedirect;
  }

  return (
    <BlueHeaderContent
      blueHeaderTitle={intl.formatMessage(messages.pageTitle)}
      backArrowHeaderButton={showBackButton ? handleBack : undefined}
      medium
    >
      <div className="intake-form-step">
        <div className="intake-form-step__content">
          <div className="intake-form-step__card-container">
            <div className="intake-form-step__progress-bar">
              <div
                className="intake-form-step__progress-bar__fill"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>

            <div className="intake-form-step__card">
              <div className="intake-form-step__title-section">
                <span className="intake-form-step__step-indicator">
                  {intl.formatMessage(messages.stepIndicator, {
                    current: stepNumber,
                    total: totalSteps,
                  })}
                </span>
                <h2 className="intake-form-step__main-title">{title}</h2>
                {subtitle && (
                  <p className="intake-form-step__subtitle">{subtitle}</p>
                )}
              </div>

              <div className="intake-form-step__body">{children}</div>

              <div className="intake-form-step__actions">
                <Button
                  className="intake-form-step__actions__continue"
                  unelevated
                  onClick={onContinue}
                  disabled={continueDisabled}
                >
                  {continueLabel || intl.formatMessage(messages.continueButton)}
                </Button>
                {showBackLink && (
                  <button
                    className="intake-form-step__actions__back"
                    onClick={handleBack}
                    aria-label={intl.formatMessage(messages.backButtonAria)}
                  >
                    {intl.formatMessage(messages.backButton)}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </BlueHeaderContent>
  );
};

export default IntakeFormStepLayout;
